import { Page } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexCollectionPageContentBlock } from '@components/_rolex/CollectionPageContentBlock'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { Item, SingleItemResponse } from '@lib/kontent'
import { getCatchAll2025ModelsPaths } from '@lib/kontent/getCatchAll2025ModelsPaths'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { NewWatches2025ModelPage as NewWatches2025ModelPageModel } from '@lib/kontent/models/NewWatches2025ModelPage'
import { RolexCollectionPageContentBlock as RolexCollectionPageContentBlockModel } from '@lib/kontent/models/RolexCollectionPageContentBlock'
import { RolexKeepExploringSliderItem } from '@lib/kontent/models/RolexKeepExploringSliderItem'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'

type NewWatches2025ModelPageProps = SingleItemResponse<NewWatches2025ModelPageModel>

const NewWatches2025ModelPage: NextPage<NewWatches2025ModelPageProps> = ({
  item,
  modular_content,
}) => {
  const { locales, locale } = useRouter()
  const { t } = useTranslation('rolex')
  const pageIntro = modular_content[item.elements.intro_text.value[0]] as Item<RolexPageIntroModel>
  const contentBlocks = item.elements.content_blocks.modular_content.map(
    (key) => modular_content[key].elements,
  ) as RolexCollectionPageContentBlockModel[]
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  ) as RolexKeepExploringSliderItem[]

  return (
    <Page
      title={t('navigation.newWatches2025')}
      languageAlternates={buildLanguageAlternateNavigation(
        locales,
        'rolex.newWatches2025',
        '/rolex',
        `/${item.elements.url_slug.value}`,
      )}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="New Watches 2025" productFamily={item.elements.url_slug.value}>
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.newWatches2025', { ns: 'rolex' }),
              href: `/rolex${t('rolex.newWatches2025', { ns: 'navigation' })}`,
            },
          ]}
          currentPageTitle={item.elements.model_title.value}
        />
        <div className="bg-rolexLightBeige">
          <RolexImage
            size="xl"
            mobileImage={item.elements.mobile_image}
            desktopImage={item.elements.desktop_image}
          />
          <RolexPageIntro
            pretitle={pageIntro.elements.pretitle__optional_?.value || ''}
            title={pageIntro.elements.title.value}
            description={pageIntro.elements.description}
            titleTag={pageIntro.elements.title_tag.value[0].codename}
          />
        </div>
        {contentBlocks.map((block, key) => (
          <RolexCollectionPageContentBlock key={key} {...block} modularContent={modular_content} />
        ))}
        <KeepExploring className="py-[30px] md:py-[60px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview, params }) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const urlSlug = params.slug as string
    const content = await getPageByTypeAndUrlSlug<NewWatches2025ModelPageModel>({
      language,
      type: 'new_watches_2025_model_page',
      urlSlug,
      preview,
    })

    return {
      props: {
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
        ...content,
      },
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = async () => {
  if (process.env.VERCEL_ENV !== 'production') {
    return { paths: [], fallback: 'blocking' }
  }

  const paths = await getCatchAll2025ModelsPaths()
  return {
    fallback: false,
    paths,
  }
}

export default NewWatches2025ModelPage
